import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Datastore, DatastoreCollection, uniq } from '@freelancer/datastore';
import {
  FreelancerReputation,
  LocalHourlyRate,
  PortfolioItem,
  PortfoliosCollection,
  ProfileCategoriesCollection,
  ProfileViewUser,
  SkillsCollection,
} from '@freelancer/datastore/collections';
import { ButtonColor } from '@freelancer/ui/button';
import { Margin } from '@freelancer/ui/margin';
import { PictureDisplay } from '@freelancer/ui/picture';
import { TextAlign, TextSize } from '@freelancer/ui/text';
import { flatten } from '@freelancer/utils';
import { HireMeUser } from 'app/hire-me/hire-me.types';
import {
  AwardProjectHireMeState,
  AwardProjectModel,
} from 'app/user-profile/user-profile-award-project.types';
import * as Rx from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

export enum PortfolioItemSize {
  SMALL,
  LARGE,
}

export enum PortfolioItemCount {
  SMALL = 6,
  LARGE = 8,
}

@Component({
  selector: 'app-user-portfolio',
  template: `
    <ng-container *ngIf="!(loadingPortfolios$ | flAsync); else loading">
      <ng-container *ngIf="portfolios$ | flAsync as portfolios; else loading">
        <fl-bit
          class="UserPortfolio-empty"
          *ngIf="portfolios.length === 0"
          [flMarginBottom]="Margin.MID"
        >
          <fl-picture
            alt="No portfolios yet"
            i18n-alt="Alt text for portfolio empty state"
            class="UserPortfolio-empty-image"
            [display]="PictureDisplay.BLOCK"
            [flMarginBottom]="Margin.SMALL"
            [src]="'portfolio/illustration.svg'"
            [lazyLoad]="true"
          ></fl-picture>
          <fl-text
            class="UserPortfolio-empty-label"
            i18n="Copy if user has no portfolio items yet."
            [size]="TextSize.SMALL"
            [textAlign]="TextAlign.CENTER"
          >
            No portfolio items have been added yet
          </fl-text>
        </fl-bit>

        <fl-bit [flMarginBottom]="Margin.XXSMALL">
          <fl-grid>
            <fl-col
              *ngFor="let item of portfolios; trackBy: trackById"
              [col]="12"
              [colTablet]="
                portfolioItemSize === PortfolioItemSize.SMALL ? 4 : 6
              "
            >
              <fl-bit [flMarginBottom]="Margin.MID">
                <app-portfolio-item-card
                  [awardProjectBid$]="awardProjectBid$"
                  [awardProjectHireMeState$]="awardProjectHireMeState$"
                  [categoriesCollection]="categoriesCollection"
                  [hireMeUser$]="hireMeUser$"
                  [isLoggedIn$]="isLoggedIn$"
                  [localHourlyRate$]="localHourlyRate$"
                  [portfolioItem]="item"
                  [portfolioItemSize]="portfolioItemSize"
                  [reputation$]="reputation$"
                  [showModalOnClick]="showModalOnClick"
                  [skillsCollection]="skillsCollection"
                  [user$]="user$"
                ></app-portfolio-item-card>
              </fl-bit>
            </fl-col>
          </fl-grid>
        </fl-bit>

        <fl-bit class="UserPortfolio-actions">
          <ng-container *ngIf="canViewMore$ | flAsync">
            <ng-container *ngIf="isLoggedIn$ | flAsync; else loggedOutMessage">
              <fl-button
                class="UserPortfolio-view-more"
                *ngIf="canViewMore$ | flAsync"
                flTrackingSection="UserPortfolio"
                flTrackingLabel="UserPortfolioFetchMoreButton"
                i18n="View more portfolio items button"
                [busy]="isFetchingPortfolios$ | flAsync"
                [color]="ButtonColor.DEFAULT"
                (click)="onViewMorePortfolios()"
              >
                Load more
              </fl-button>
            </ng-container>

            <ng-template #loggedOutMessage>
              <app-user-profile-logged-out-link
                [username]="(user$ | flAsync)?.username"
                [flTrackingLabel]="
                  'LoggedOutUserProfilesPortfolioViewFullProfile'
                "
              >
              </app-user-profile-logged-out-link>
            </ng-template>
          </ng-container>
        </fl-bit>
      </ng-container>
    </ng-container>
    <ng-template #loading>
      <fl-grid>
        <fl-col
          *flRepeat="6"
          [col]="12"
          [colTablet]="portfolioItemSize === PortfolioItemSize.SMALL ? 4 : 6"
        >
          <fl-card [flMarginBottom]="Margin.MID">
            <fl-bit class="LoadingContainer-image">
              <fl-loading-text [padded]="false"></fl-loading-text>
            </fl-bit>
            <fl-bit class="LoadingContainer-title">
              <fl-loading-text [rows]="1" [padded]="false"></fl-loading-text>
            </fl-bit>
          </fl-card>
        </fl-col>
      </fl-grid>
    </ng-template>
  `,
  styleUrls: ['./user-portfolio.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserPortfolioComponent implements OnInit, OnDestroy {
  ButtonColor = ButtonColor;
  TextSize = TextSize;
  Margin = Margin;
  PictureDisplay = PictureDisplay;
  PortfolioItemSize = PortfolioItemSize;
  TextAlign = TextAlign;

  @Input() awardProjectBid$: Rx.Observable<AwardProjectModel>;
  @Input() awardProjectHireMeState$: Rx.Observable<AwardProjectHireMeState>;
  @Input()
  categoriesCollection: DatastoreCollection<ProfileCategoriesCollection>;
  @Input() categoryId$: Rx.Observable<number> = Rx.of(-1);
  @Input() hireMeUser$?: Rx.Observable<HireMeUser>;
  @Input() initialItemCount: PortfolioItemCount = PortfolioItemCount.LARGE;
  @Input() isCorporate$: Rx.Observable<boolean> = Rx.of(false);
  @Input() isLoggedIn$: Rx.Observable<boolean>;
  @Input() localHourlyRate$: Rx.Observable<LocalHourlyRate | undefined>;
  @Input() portfolioItemSize: PortfolioItemSize = PortfolioItemSize.LARGE;
  @Input() reputation$: Rx.Observable<FreelancerReputation>;
  @Input() showModalOnClick?: boolean;
  @Input() user$: Rx.Observable<ProfileViewUser>;

  portfoliosCollection: DatastoreCollection<PortfoliosCollection>;
  skillsCollection: DatastoreCollection<SkillsCollection>;
  canViewMore$: Rx.Observable<boolean>;
  isFetchingPortfolios$: Rx.Observable<boolean>;
  portfolios$: Rx.Observable<readonly PortfolioItem[]>;
  private _isFetchingPortfoliosSubject$: Rx.BehaviorSubject<boolean>;
  private _isFetchingPortfoliosSubscription?: Rx.Subscription;
  private _limit$: Rx.BehaviorSubject<number>;
  displayAmount$: Rx.Observable<number>;

  loadingPortfolios$: Rx.Observable<boolean>;

  constructor(private datastore: Datastore) {}

  ngOnInit() {
    const userId$ = this.user$.pipe(map(u => u.id));

    this._limit$ = new Rx.BehaviorSubject<number>(
      this.initialItemCount + this.initialItemCount,
    );

    this.displayAmount$ = this._limit$
      .asObservable()
      .pipe(map(limit => limit - this.initialItemCount));

    this._isFetchingPortfoliosSubject$ = new Rx.BehaviorSubject<boolean>(true);

    this.isFetchingPortfolios$ = this._isFetchingPortfoliosSubject$.asObservable();

    this.portfoliosCollection = this.datastore.collection<PortfoliosCollection>(
      'portfolios',
      query =>
        Rx.combineLatest([this.isCorporate$, this.categoryId$]).pipe(
          map(([isCorporate, categoryId]) =>
            isCorporate && categoryId !== -1
              ? query
                  .where('userId', '==', userId$)
                  .where('categories', 'intersects', [categoryId])
                  .limit(this._limit$)
              : query.where('userId', '==', userId$).limit(this._limit$),
          ),
        ),
    );

    this.loadingPortfolios$ = this.portfoliosCollection.status$.pipe(
      map(status => !status.ready && !status.error),
    );

    this.canViewMore$ = Rx.combineLatest([
      this.portfoliosCollection.valueChanges(),
      this._limit$,
      this.displayAmount$,
      this._isFetchingPortfoliosSubject$,
    ]).pipe(
      map(
        ([items, limit, displayAmount, isFetching]) =>
          items.length > limit - (limit - displayAmount) || isFetching,
      ),
    );

    this._isFetchingPortfoliosSubscription = this.portfoliosCollection
      .valueChanges()
      .pipe(
        distinctUntilChanged(),
        map(() => this._isFetchingPortfoliosSubject$.next(false)),
      )
      .subscribe();

    // Only show the specified amount of items
    this.portfolios$ = Rx.combineLatest([
      this.portfoliosCollection.valueChanges(),
      this.displayAmount$,
    ]).pipe(map(([items, displayAmount]) => items.slice(0, displayAmount)));

    const skillsIds$ = this.portfolios$.pipe(
      map(items => uniq(flatten(items.map(item => item.skillIds)))),
    );

    this.skillsCollection = this.datastore.collection<SkillsCollection>(
      'skills',
      skillsIds$,
    );
  }

  ngOnDestroy() {
    if (this._isFetchingPortfoliosSubscription) {
      this._isFetchingPortfoliosSubscription.unsubscribe();
    }
  }

  onViewMorePortfolios() {
    this._isFetchingPortfoliosSubject$.next(true);
    this._limit$.next(this._limit$.getValue() + this.initialItemCount);
  }

  trackById(index: number, item: PortfolioItem): number {
    return item.id;
  }
}
